import React, { useState } from "react";
import { toast } from "sonner";
import { submitContact } from "@/lib/api";
import { Loader2, Check } from "lucide-react";

const supportTypes = [
  "Business Audit",
  "AI Readiness Assessment",
  "POP\u2122 Intelligence",
  "Executive Advisory",
  "Not Sure Yet",
];

const initial = {
  name: "",
  email: "",
  business_name: "",
  website: "",
  support_type: "",
  message: "",
};

const validate = (v) => {
  const e = {};
  if (!v.name.trim()) e.name = "Required";
  if (!/^\S+@\S+\.\S+$/.test(v.email)) e.email = "Enter a valid email";
  if (!v.support_type) e.support_type = "Choose a path";
  if (!v.message.trim() || v.message.trim().length < 6)
    e.message = "Tell us a little more";
  return e;
};

export const ContactForm = () => {
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (k) => (e) => setValues((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(values);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    try {
      setLoading(true);
      await submitContact(values);
      setSuccess(true);
      toast.success("Thanks — BOOMM will be in touch shortly.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong sending your message. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div
        className="card-edge p-10 md:p-12 rounded-[2px]"
        data-testid="contact-success"
      >
        <div className="w-12 h-12 rounded-full border border-[#B58B42] inline-flex items-center justify-center text-[#B58B42]">
          <Check size={22} />
        </div>
        <h3 className="font-display text-2xl md:text-3xl mt-6 text-[#2A2422]">
          Message received.
        </h3>
        <p className="font-body text-[#50616B] mt-4 leading-relaxed">
          BOOMM will review your note and respond from{" "}
          <span className="text-[#2A2422] font-medium">marjorie@boommgroup.com</span>{" "}
          within two business days.
        </p>
        <button
          className="mt-8 btn-secondary px-6 py-3 text-sm tracking-wide rounded-[2px]"
          onClick={() => {
            setValues(initial);
            setSuccess(false);
          }}
          data-testid="contact-reset"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="card-edge p-8 md:p-10 rounded-[2px]"
      data-testid="contact-form"
      noValidate
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="field-label" htmlFor="c_name">Name</label>
          <input
            id="c_name"
            data-testid="contact-name"
            className="field-input"
            type="text"
            value={values.name}
            onChange={set("name")}
            placeholder="Your name"
          />
          {errors.name && (
            <p className="mt-1 text-xs text-[#9C3B2A]">{errors.name}</p>
          )}
        </div>
        <div>
          <label className="field-label" htmlFor="c_email">Email</label>
          <input
            id="c_email"
            data-testid="contact-email"
            className="field-input"
            type="email"
            value={values.email}
            onChange={set("email")}
            placeholder="you@business.com"
          />
          {errors.email && (
            <p className="mt-1 text-xs text-[#9C3B2A]">{errors.email}</p>
          )}
        </div>
        <div>
          <label className="field-label" htmlFor="c_business">Business Name</label>
          <input
            id="c_business"
            data-testid="contact-business-name"
            className="field-input"
            type="text"
            value={values.business_name}
            onChange={set("business_name")}
            placeholder="Optional"
          />
        </div>
        <div>
          <label className="field-label" htmlFor="c_website">Website</label>
          <input
            id="c_website"
            data-testid="contact-website"
            className="field-input"
            type="text"
            value={values.website}
            onChange={set("website")}
            placeholder="Optional"
          />
        </div>
        <div className="md:col-span-2">
          <label className="field-label" htmlFor="c_support">
            What kind of support are you interested in?
          </label>
          <select
            id="c_support"
            data-testid="contact-support-type"
            className="field-input"
            value={values.support_type}
            onChange={set("support_type")}
          >
            <option value="">Select an option</option>
            {supportTypes.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          {errors.support_type && (
            <p className="mt-1 text-xs text-[#9C3B2A]">{errors.support_type}</p>
          )}
        </div>
        <div className="md:col-span-2">
          <label className="field-label" htmlFor="c_message">Message</label>
          <textarea
            id="c_message"
            data-testid="contact-message"
            className="field-input"
            value={values.message}
            onChange={set("message")}
            placeholder="Where is your business feeling unclear, overloaded, or ready for its next level of momentum?"
          />
          {errors.message && (
            <p className="mt-1 text-xs text-[#9C3B2A]">{errors.message}</p>
          )}
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="contact-submit"
        className="mt-8 btn-primary px-7 py-3.5 text-sm tracking-wide rounded-[2px] inline-flex items-center gap-2 disabled:opacity-70"
      >
        {loading && <Loader2 size={16} className="animate-spin" />}
        Spark Better Decisions
      </button>
    </form>
  );
};

export default ContactForm;
